import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFileExcel, faUpload } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { downloadTemplateXlsx } from '../../utils/xlsxExport';
import * as XLSX from 'xlsx';
import ConfirmDialog from './ConfirmDialog';
import { useTranslation } from 'react-i18next';

export default function ImportModal({
  isOpen,
  onClose,
  onImport,
  importing = false,
  importError = null,
  importSuccess = false,
  templateHeaders = [],
  templateColumnSpecs = null,
  templateFilename,
  inputId,
  skipHeaderValidation = false,
}) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const inputRef = useRef(null);

  const effectiveTemplateFilename = templateFilename ?? t('common.dialogs.import.config.defaultTemplateFilename');
  const effectiveInputId = inputId ?? t('common.dialogs.import.config.defaultInputId');

  // Localized strings via react-i18next
  const s = {
    title: t('common.dialogs.import.title'),
    subtitle: t('common.dialogs.import.subtitle'),
    description: t('common.dialogs.import.description'),
    uploadLabel: t('common.dialogs.import.uploadLabel'),
    helper: t('common.dialogs.import.helper'),
    status: {
      success: t('common.dialogs.import.status.success'),
      error: t('common.dialogs.import.status.error'),
      uploading: t('common.dialogs.import.status.uploading'),
      ready: t('common.dialogs.import.status.ready'),
    },
    errors: {
      invalidType: t('common.dialogs.import.errors.invalidType'),
      noFile: t('common.dialogs.import.errors.noFile'),
      maxSize: t('common.dialogs.import.errors.maxSize'),
    },
    buttons: {
      cancel: t('common.dialogs.import.buttons.cancel'),
      import: t('common.dialogs.import.buttons.import'),
      importing: t('common.dialogs.import.buttons.importing'),
      downloadTemplate: t('common.dialogs.import.buttons.downloadTemplate'),
    },
  };

  const resetState = () => {
    setSelectedFile(null);
    setValidationError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const [showErrorsDialog, setShowErrorsDialog] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (!importing) {
      resetState();
      onClose?.();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!t('common.dialogs.import.config.allowedExtensions', { returnObjects: true }).includes(extension || '')) {
      setValidationError(s.errors.invalidType);
      setSelectedFile(null);
      return;
    }

    const MAX_BYTES = t('common.dialogs.import.config.maxFileSizeBytes', { returnObjects: true });
    if (file.size > MAX_BYTES) {
      setValidationError(s.errors.maxSize);
      setSelectedFile(null);
      return;
    }

    setValidationError(null);
    setSelectedFile(file);
  };

  // Validate Excel headers against provided templateHeaders
  const validateFileHeaders = async (file) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[firstSheetName];
      const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });
      const headerRow = Array.isArray(aoa) && aoa.length > 0 ? aoa[0].map(h => String(h).trim()) : [];

      // Normalize template headers as strings trimmed
      const expected = (templateHeaders || []).map(h => String(h).trim());

      // Compare sets: find missing and extra
      const missing = expected.filter(h => !headerRow.includes(h));
      const extra = headerRow.filter(h => !expected.includes(h));

      if (missing.length === 0 && extra.length === 0) return { valid: true };

      return { valid: false, missing, extra };
    } catch (err) {
      console.error('Failed to validate file headers:', err);
      return { valid: false, error: t('common.dialogs.import.errors.parseError') };
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setValidationError(s.errors.noFile);
      return;
    }

    setValidationError(null);

    // If templateHeaders are provided and skipHeaderValidation is false, validate headers before importing
    if (!skipHeaderValidation && templateHeaders && templateHeaders.length > 0) {
      const headerCheck = await validateFileHeaders(selectedFile);
      if (!headerCheck.valid) {
        if (headerCheck.error) {
          setValidationError(headerCheck.error);
        } else {
          const title = t('common.dialogs.import.messages.columnsMismatch');

          const missingList = Array.isArray(headerCheck.missing) && headerCheck.missing.length > 0 ? headerCheck.missing.join(', ') : '';
          const extraList = Array.isArray(headerCheck.extra) && headerCheck.extra.length > 0 ? headerCheck.extra.join(', ') : '';

          // Detail template contains placeholders {missing} and {extra}.
          let detail = t('common.dialogs.import.messages.columnsMismatchDetail', { missing: missingList, extra: extraList }).trim();
          // Clean up stray punctuation/spacing when one of the lists is empty
          detail = detail.replace(/\s+\./g, '.').replace(/\s+,/g, ',').replace(/\s+/g, ' ').trim();

          const message = missingList || extraList ? `${title} ${detail}` : title;
          setValidationError(message);
        }
        return;
      }
    }

    const result = await onImport?.(selectedFile);
    if (result?.success) {
      resetState();
      onClose?.();
      return;
    }

    const err = result?.error;
    if (Array.isArray(err) && err.length > 0) {
      setServerErrors(err);
      setShowErrorsDialog(true);
    }
  };

  const handleDownloadTemplate = () => {
    const filename = `${effectiveTemplateFilename.replace(/\.xls$|\.xlsx$/i, '')}`;
    downloadTemplateXlsx(templateHeaders, filename, { columnSpecs: templateColumnSpecs });
  };

  const statusMessage = importSuccess
    ? s.status.success
    : importError
    ? s.status.error
    : importing
    ? s.status.uploading
    : s.status.ready;

  const statusColor = importSuccess
    ? UI_COLORS.STATUS.SUCCESS
    : importError
    ? UI_COLORS.STATUS.ERROR
    : UI_COLORS.STATUS.INFO;

  const downloadButtonColors = UI_COLORS.BUTTONS.SECONDARY || UI_COLORS.BUTTONS.PRIMARY;

  return ( <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {s.title}
            </p>
            <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {s.subtitle}
            </h2>
            <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {s.description}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
            disabled={importing}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          <div
            className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center"
            style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.4) }}
          >
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.15),
                color: UI_COLORS.NAVIGATION.ACTIVE,
              }}
            >
              <FontAwesomeIcon icon={faFileExcel} size="2x" />
            </div>
            <p className="mt-4 text-base font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {s.uploadLabel}
            </p>
            <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {s.helper}
            </p>
            <div className="mt-5 flex flex-col items-center gap-3">
              <input
                ref={inputRef}
                type="file"
                accept={t('common.dialogs.import.config.acceptAttribute')}
                onChange={handleFileChange}
                className="hidden"
                id={effectiveInputId}
              />
              <label
                htmlFor={effectiveInputId}
                className="inline-flex cursor-pointer items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                  color: UI_COLORS.BUTTONS.PRIMARY.text,
                }}
              >
                <FontAwesomeIcon icon={faUpload} />
                {s.uploadLabel}
              </label>
              {selectedFile && (
                <p className="text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                  {selectedFile.name}
                </p>
              )}
            </div>
          </div>

          <div
            className="flex flex-col gap-3 rounded-xl px-4 py-3 text-sm md:flex-row md:items-center md:justify-between"
            style={{
              backgroundColor: colorHelpers.withOpacity(statusColor, 0.1),
              color: statusColor,
            }}
          >
            <span>{statusMessage}</span>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105"
              style={{
                backgroundColor: downloadButtonColors.background,
                color: downloadButtonColors.text,
                border: `1px solid ${colorHelpers.withOpacity(downloadButtonColors.background, 0.3)}`,
              }}
            >
              {s.buttons.downloadTemplate}
            </button>
          </div>

          {(validationError || importError) && (
            <div
              className="rounded-xl px-4 py-3 text-sm"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.15),
                color: UI_COLORS.STATUS.ERROR,
              }}
            >
              {validationError && <div>{validationError}</div>}
              {Array.isArray(importError) ? (
                <div>
                  <div className="font-semibold mb-2">{t('common.dialogs.import.status.error')}:</div>
                  <ul className="list-disc list-inside">
                    {importError.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                importError && <div>{importError}</div>
              )}
            </div>
          )}
        </div>

        <div
          className="flex items-center justify-end gap-3 border-t px-6 py-4"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <button
            type="button"
            onClick={handleClose}
            className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: 'transparent',
              color: UI_COLORS.TEXT.MUTED,
              border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
            }}
            disabled={importing}
          >
            {s.buttons.cancel}
          </button>
          <button
            type="button"
            onClick={handleImport}
            className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
              color: UI_COLORS.BUTTONS.PRIMARY.text,
            }}
            disabled={importing}
          >
            {importing ? s.buttons.importing : s.buttons.import}
          </button>
        </div>
      </div>
    </div>
    {showErrorsDialog && (
      <ConfirmDialog
        isOpen
        title={s.status.error}
        message={(
          <div>
            <div className="font-semibold mb-2">{t('common.dialogs.import.status.error')}:</div>
            <ul className="list-disc list-inside max-h-60 overflow-auto">
              {serverErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}
        confirmLabel={t('common.buttons.close')}
        onConfirm={() => setShowErrorsDialog(false)}
        onCancel={() => setShowErrorsDialog(false)}
      />
    )}
  </> );
}
